export interface KpiDashboardConfig {
  emissionFactors: {
    fuels: Record<string, number>;  // kg CO2e per m³ of fuel
    electricityGrid: number;        // kg CO2e per kWh
    landfill: number;               // kg CO2e per tonne of landfill waste
  };
  // Energy content per m³ of fuel (kWh/m³). Electricity Quantitym3 is already kWh — factor = 1.
  calorificValues: Record<string, number>;
  workforce: {
    byPackage: Record<string, number>;  // package label → headcount
    default: number;
  };
  projectArea: {
    byPackage: Record<string, number>;  // package label → m² built
    default: number;
  };
  workingDaysPerMonth: number;
  workingHoursPerDay: number;
  // UTC offset for the project site (hours). returnDate() in dataProcessor.ts applies the same value.
  timezoneOffsetHours: number;
  // When true, same-day and next-day closures both count as 1 day (standard compliance SLA convention).
  // Set false to count calendar days exactly (same-day = 0 days, next-day = 1 day).
  sameAndNextDayAsOne: boolean;
  // Per-module visibility flags. Set a flag to false to hide that KPI module.
  // Legacy Db* components always render below regardless of these flags.
  modules: {
    enabled:            boolean;  // Master switch — false hides the entire KPI section; individual flags are ignored
    compliance:         boolean;  // Observation compliance & resolution KPIs
    sustainability:     boolean;  // GHG emissions, waste diversion, resource intensity
    materials:          boolean;  // Spoil balance, recycled & regional material %
    nuisance:           boolean;  // Noise / vibration / dust breach tracking
    workforce:          boolean;  // Training coverage & toolbox talk frequency
    packageLeaderboard: boolean;  // Cross-package performance comparison table
  };
  // Site nodes whose title contains any of these words are excluded from the package
  // dropdown/filter (PortfolioSideNav, Dashboard). Global — not project-scoped.
  excludedPackageKeywords: string[];
}

// Shape of the "kpiConfig" backend config (api/config/kpiConfig, config_value JSON).
// sustainability is global (no project scoping); pm is per-project with an org-wide default
// that every project falls back to for any field it doesn't explicitly override.
export interface RemoteKpiProjectOverride {
  workforce?: { default?: number; byPackage?: Record<string, number> };
  projectArea?: { default?: number; byPackage?: Record<string, number> };
  workingDaysPerMonth?: number;
  workingHoursPerDay?: number;
  sameAndNextDayAsOne?: boolean;
  modules?: Partial<KpiDashboardConfig['modules']>;
}

export interface RemoteKpiConfig {
  sustainability?: {
    fuels?: Record<string, number>;
    electricityGrid?: number;
    landfill?: number;
  };
  pm?: {
    default?: RemoteKpiProjectOverride;
    projects?: Record<string, RemoteKpiProjectOverride>;
  };
  // Global, not project-scoped — same tier as `sustainability`.
  excludedPackageKeywords?: string[];
}

// GUIDs are canonically case-insensitive; api/sites and the seeded config row aren't
// guaranteed to agree on casing, so match project keys without regard to case.
function findProjectOverride(
  projects: Record<string, RemoteKpiProjectOverride> | undefined,
  projectId: string | null,
): RemoteKpiProjectOverride | undefined {
  if (!projects || !projectId) return undefined;
  if (projects[projectId]) return projects[projectId];
  const lower = projectId.toLowerCase();
  const matchKey = Object.keys(projects).find(k => k.toLowerCase() === lower);
  return matchKey ? projects[matchKey] : undefined;
}

function mergeByPackage(
  base: { default: number; byPackage: Record<string, number> },
  override?: { default?: number; byPackage?: Record<string, number> },
): { default: number; byPackage: Record<string, number> } {
  if (!override) return base;
  return {
    default: override.default ?? base.default,
    byPackage: { ...base.byPackage, ...(override.byPackage ?? {}) },
  };
}

const isDevRuntime = typeof __DEV__ === 'undefined' || __DEV__;

type ConfigSource = 'project' | 'org-default' | 'hardcoded';

function sourceOf(projectVal: unknown, defaultVal: unknown): ConfigSource {
  if (projectVal !== undefined) return 'project';
  if (defaultVal !== undefined) return 'org-default';
  return 'hardcoded';
}

// Diagnostic only — prints a source breakdown so it's obvious at a glance which fields came
// from the backend config vs the hardcoded fallback in this file. Safe to remove once the
// backend-driven config is trusted; has no effect on the resolved values themselves.
function logKpiConfigProvenance(
  remote: RemoteKpiConfig,
  pmDefault: RemoteKpiProjectOverride,
  pmProject: RemoteKpiProjectOverride,
  projectId: string | null,
): void {
  if (!isDevRuntime) return;

  const byPackageSource = (project?: Record<string, number>, org?: Record<string, number>): ConfigSource | 'hardcoded (empty)' => {
    if (project && Object.keys(project).length) return 'project';
    if (org && Object.keys(org).length) return 'org-default';
    return 'hardcoded (empty)';
  };

  const rows: Record<string, string> = {
    'workforce.default':               sourceOf(pmProject.workforce?.default, pmDefault.workforce?.default),
    'workforce.byPackage':              byPackageSource(pmProject.workforce?.byPackage, pmDefault.workforce?.byPackage),
    'projectArea.default':             sourceOf(pmProject.projectArea?.default, pmDefault.projectArea?.default),
    'projectArea.byPackage':           byPackageSource(pmProject.projectArea?.byPackage, pmDefault.projectArea?.byPackage),
    'workingDaysPerMonth':             sourceOf(pmProject.workingDaysPerMonth, pmDefault.workingDaysPerMonth),
    'workingHoursPerDay':              sourceOf(pmProject.workingHoursPerDay, pmDefault.workingHoursPerDay),
    'sameAndNextDayAsOne':             sourceOf(pmProject.sameAndNextDayAsOne, pmDefault.sameAndNextDayAsOne),
    'emissionFactors.electricityGrid': remote.sustainability?.electricityGrid !== undefined ? 'backend' : 'hardcoded',
    'emissionFactors.landfill':        remote.sustainability?.landfill !== undefined ? 'backend' : 'hardcoded',
    'emissionFactors.fuels':           remote.sustainability?.fuels ? `backend (${Object.keys(remote.sustainability.fuels).length} fuels overridden)` : 'hardcoded',
    'calorificValues':                 'hardcoded — not in backend schema by design',
    'timezoneOffsetHours':             'hardcoded — not in backend schema by design',
    'excludedPackageKeywords':         remote.excludedPackageKeywords ? `backend (${remote.excludedPackageKeywords.length} keywords)` : 'hardcoded',
  };
  (Object.keys(DEFAULT_KPI_CONFIG.modules) as (keyof KpiDashboardConfig['modules'])[]).forEach(key => {
    rows[`modules.${key}`] = sourceOf(pmProject.modules?.[key], pmDefault.modules?.[key]);
  });

  // eslint-disable-next-line no-console
  console.groupCollapsed(`[kpiConfig] source breakdown for project ${projectId ?? '(none)'}`);
  // eslint-disable-next-line no-console
  console.table(rows);
  // eslint-disable-next-line no-console
  console.groupEnd();
}

// Resolves hardcoded DEFAULT_KPI_CONFIG <- remote.pm.default <- remote.pm.projects[projectId].
// Each level only overrides what it defines — byPackage maps merge key-by-key, not wholesale.
export function resolveKpiConfig(
  remote: RemoteKpiConfig | null | undefined,
  projectId: string | null,
): KpiDashboardConfig {
  if (!remote) {
    if (isDevRuntime) {
      // eslint-disable-next-line no-console
      console.warn(`[kpiConfig] backend config unavailable for project ${projectId ?? '(none)'} — rendering 100% hardcoded DEFAULT_KPI_CONFIG`);
    }
    return DEFAULT_KPI_CONFIG;
  }

  const pmDefault = remote.pm?.default ?? {};
  const pmProject = findProjectOverride(remote.pm?.projects, projectId) ?? {};
  logKpiConfigProvenance(remote, pmDefault, pmProject, projectId);

  const workforce = mergeByPackage(
    mergeByPackage(DEFAULT_KPI_CONFIG.workforce, pmDefault.workforce),
    pmProject.workforce,
  );
  const projectArea = mergeByPackage(
    mergeByPackage(DEFAULT_KPI_CONFIG.projectArea, pmDefault.projectArea),
    pmProject.projectArea,
  );

  return {
    ...DEFAULT_KPI_CONFIG,
    emissionFactors: {
      fuels: { ...DEFAULT_KPI_CONFIG.emissionFactors.fuels, ...(remote.sustainability?.fuels ?? {}) },
      electricityGrid: remote.sustainability?.electricityGrid ?? DEFAULT_KPI_CONFIG.emissionFactors.electricityGrid,
      landfill: remote.sustainability?.landfill ?? DEFAULT_KPI_CONFIG.emissionFactors.landfill,
    },
    workforce,
    projectArea,
    workingDaysPerMonth: pmProject.workingDaysPerMonth ?? pmDefault.workingDaysPerMonth ?? DEFAULT_KPI_CONFIG.workingDaysPerMonth,
    workingHoursPerDay: pmProject.workingHoursPerDay ?? pmDefault.workingHoursPerDay ?? DEFAULT_KPI_CONFIG.workingHoursPerDay,
    sameAndNextDayAsOne: pmProject.sameAndNextDayAsOne ?? pmDefault.sameAndNextDayAsOne ?? DEFAULT_KPI_CONFIG.sameAndNextDayAsOne,
    modules: { ...DEFAULT_KPI_CONFIG.modules, ...pmDefault.modules, ...pmProject.modules },
    excludedPackageKeywords: remote.excludedPackageKeywords ?? DEFAULT_KPI_CONFIG.excludedPackageKeywords,
  };
}

// Default values — code-level fallback used when the backend config is unavailable or a
// project/field isn't covered by it yet.
export const DEFAULT_KPI_CONFIG: KpiDashboardConfig = {
  emissionFactors: {
    fuels: {
      'Diesel':           2.68,
      'Diesel (offroad)': 2.68,
      'Petrol':           2.31,
      'LPG':              1.51,
      'Natural Gas':      2.02,
      'HFO':              3.17,
      'CNG':              2.10,
      'Biodiesel':        0.17,
    },
    electricityGrid: 0.44,  // UAE grid average kg CO2e / kWh
    landfill: 0.467,        // kg CO2e / tonne
  },
  // kWh per m³ of fuel (net calorific values). Electricity is already kWh so factor = 1.
  calorificValues: {
    'Diesel':           9940,
    'Diesel (offroad)': 9940,
    'Petrol':           8940,
    'LPG':              7030,
    'Natural Gas':        10.55,  // gas at atmospheric pressure (kWh/m³)
    'HFO':             11280,
    'CNG':                10.55,
    'Biodiesel':         9060,
  },
  workforce: {
    byPackage: {},
    default: 100,
  },
  projectArea: {
    byPackage: {},
    default: 10000,
  },
  workingDaysPerMonth: 25,
  workingHoursPerDay: 8,
  timezoneOffsetHours: 4,   // UAE / Gulf Standard Time (UTC+4)
  sameAndNextDayAsOne: true, // same-day and next-day closures = 1 day (compliance SLA convention)
  modules: {
    enabled:            true,
    compliance:         true,
    sustainability:     false,
    materials:          true,
    nuisance:           true,
    workforce:          true,
    packageLeaderboard: true,
  },
  excludedPackageKeywords: ['Induction', 'Starter pack', 'Reports'],
};
